import { ErrorOutline } from "@mui/icons-material";
import { Box, Icon, Stack, Typography } from "@mui/material";
import React from "react";
import { PoweredOff, PoweredOn } from "src/components/UI/DeviceBadge";
import { DeviceIcon, Img } from "src/components/UI/DeviceCard";
import { AlenBadges } from "src/components/UI/DeviceCard/Badges";
import FilterLife from "src/components/UI/DeviceCard/FilterLife";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import { DeviceType } from "src/interfaces/IDevice";
import AlenProductConfig from "src/services/alen/alenDeviceConfig";
import {
  flattenProductConfig,
  getAlenDeviceShadow,
} from "src/services/alen/alenDeviceConfig/utils";
import { BORDER_COLOR_CARD } from "src/styles";
import CardActionMenu from "src/components/UI/CardActionMenu";
import useFirmwareUpdateContext from "./FirmwareUpdate/FirmwareUpdateContext";

export function AlenDeviceImage({ url }: { url: string }) {
  return (
    <Img
      src={`${process.env.PUBLIC_URL}/icons/alen_device_images/${url}`}
      alt="device-icon"
      sx={{ objectFit: "contain" }}
    />
  );
}

export function AlenDeviceIcon({ iconUrl }: { iconUrl: string }) {
  return (
    <Icon sx={{ marginRight: 1, display: "flex", alignItems: "center" }}>
      <AlenDeviceImage url={iconUrl} />
    </Icon>
  );
}
type CardProps = {
  device: AlenDevice;
  checkbox: React.ReactNode;
  checked: boolean;
  onStatusClick?: (standardName: string) => void;
};
function AlenDeviceCard({
  device,
  checkbox,
  checked,
  onStatusClick,
}: CardProps) {
  const shadow = getAlenDeviceShadow(device, AlenProductConfig);
  const filterLife =
    shadow.filter_life !== undefined
      ? parseInt(shadow.filter_life as string, 10)
      : undefined;

  const iconUrl = flattenProductConfig(AlenProductConfig).find(
    (c) => c.productInfo.pid === device.product_id
  )?.productInfo.image;

  const { checkFirmwareUpdateStatus } = useFirmwareUpdateContext();
  return (
    <Stack
      direction="row"
      sx={[
        {
          borderRadius: "4px",
          width: "408px",
          padding: 3,
          paddingBottom: 2,
          paddingTop: 2,
        },
        checked
          ? {
              border: 2,
              margin: "0px",
              borderColor: "#2DCCD3",
              backgroundColor: "#F5FDFD",
            }
          : {
              border: 1,
              // use margin to prevent box moved when checked/unchecked
              margin: "1px",
              borderColor: BORDER_COLOR_CARD,
              backgroundColor: "white",
            },
      ]}
    >
      <>
        {checkbox}
        <Stack direction="column" sx={{ flex: 1 }}>
          <Stack
            direction="row"
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginBottom: 1,
            }}
          >
            {iconUrl ? (
              <AlenDeviceIcon iconUrl={iconUrl} />
            ) : (
              <DeviceIcon deviceType={DeviceType.ALEN} />
            )}
            <Stack
              direction="row"
              sx={{
                flexGrow: 1,
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1,
              }}
            >
              {shadow.Power === "On" ? (
                <PoweredOn
                  onClick={
                    onStatusClick ? () => onStatusClick("Power") : undefined
                  }
                />
              ) : (
                <PoweredOff
                  onClick={
                    onStatusClick ? () => onStatusClick("Power") : undefined
                  }
                />
              )}
            </Stack>
            {filterLife !== undefined && !Number.isNaN(filterLife) ? (
              <FilterLife
                sx={{ marginRight: "1rem", marginTop: ".5rem" }}
                life={filterLife / 100}
              />
            ) : null}
            <CardActionMenu
              options={[
                {
                  label: "Check firmware update",
                  onClick: () => checkFirmwareUpdateStatus(device),
                },
              ]}
            />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: "space-between" }}>
            <Stack direction="column" sx={{ alignItems: "flex-start" }} gap={1}>
              <Typography variant="subtitle2">
                {device.location_name}
              </Typography>
              <Typography variant="subtitle4">{device.product_name}</Typography>
              <Typography variant="subtitle4">{device.name}</Typography>
              <AlenBadges shadow={shadow} onClick={onStatusClick} />
              {!device.online && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: ".25rem" }}
                >
                  <Typography color="error" variant="subtitle4">
                    Communication Error
                  </Typography>
                  <ErrorOutline fontSize="inherit" color="error" />
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </>
    </Stack>
  );
}

export default AlenDeviceCard;
